import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';

import { accumulatorTypes, createEmptyAccumulator } from 'utils/accumulators';
import {
  DataBlocks,
  Button,
  CollapsedContent,
  HTML,
  Preloader,
  Icon,
} from 'components';

import AccumulatorBlock from './components/AccumulatorBlock';
import BuyPackagesPopup from './components/BuyPackagesPopup';
import AccumulatorPopup from './components/AccumulatorPopup';
import ServiceTile from './components/ServiceTile';
import ServicePopup from './components/ServicePopup';

import styles from './styles.pcss';
const cx = classNames.bind(styles);

const requiredAccTypes = [
  accumulatorTypes.kbyte,
  accumulatorTypes.seconds,
  accumulatorTypes.sms,
];

const YouthTariff = ({
  data = {
    listForYoung: [],
  },
  content = {},
  packages = [],
  services = [],
  tariffName,
}) => {
  const [isBuyPackagesPopupOpen, setIsBuyPackagesPopupOpen] = useState(false);
  const [activeAccumulatorIndex, setActiveAccumulatorIndex] = useState(null);
  const [activeService, setActiveService] = useState(null);
  const [optionsBlockOpened, setOptionsBlockOpened] = useState(false);
  const onAccumulatorClick = (i) => setActiveAccumulatorIndex(i);
  const onAccumulatorPopupClose = () => setActiveAccumulatorIndex(null);
  const [servicesLocal, setServicesLocal] = useState(services);

  const [itemsPending, setItemsPending] = useState({
    service: null,
    packages: false,
  });

  const withPromoTikTok = useMemo(
    () => services.some(({ serviceType }) => serviceType === 'PromoTiktok'),
    [services],
  );
  const onServiceChange = (soc) => {
    setActiveService(soc);
  };
  const onServicePopupClose = () => {
    setActiveService(null);
  };

  if (!data || !content) return null;

  const accumulatorsWithRequired = useMemo(
    () => requiredAccTypes
      .map((type) => {
        const matchedAcc = data.listForYoung.find(
          ({ unit }) => unit === type,
        );

        if (matchedAcc) return matchedAcc;

        return createEmptyAccumulator({ unit: type });
      })
      .concat(
        data.listForYoung.filter(
          ({ unit }) => !requiredAccTypes.includes(unit),
        ),
      ),
    [data.listForYoung],
  );
  const activeAccumulator = activeAccumulatorIndex >= 0
    ? accumulatorsWithRequired[activeAccumulatorIndex]
    : null;
  const packagesForActiveAcc = activeAccumulator
    && packages.find(({ type }) => type === activeAccumulator.unit);

  const connectedServices = servicesLocal.filter(({ connected }) => connected);

  useEffect(() => {
    if (window && document.location.href.includes('buyPackagesPopupOpen')) {
      setIsBuyPackagesPopupOpen(true);
      window.history.replaceState(null, null, '/');
    }
  }, [window]);

  return (
    <DataBlocks.Block className={cx('wrap')}>
      <HTML as="div" className={cx('label')}>
        {content.titleLabel}
      </HTML>
      <HTML as="div" className={cx('title')}>
        {tariffName}
      </HTML>
      <HTML as="div" className={cx('description')}>
        {content.description}
      </HTML>

      <div className={cx('accumulators')}>
        {itemsPending.packages ? (
          <div className={cx('accumulatorsPending')}>
            <Preloader size="40" />
          </div>
        ) : (
          <>
            {accumulatorsWithRequired.map((acc, i) => (
              <div className={cx('accumulator')}>
                <AccumulatorBlock
                  key={i}
                  acc={acc}
                  onClick={() => onAccumulatorClick(i)}
                  content={content.accumulatorBlock}
                />
              </div>
            ))}
          </>
        )}
      </div>

      {withPromoTikTok && content.highlightedParameter && (
        <div className={cx('highlightedParameter')}>
          {content.highlightedParameter.icon && (
            <div className={cx('highlightedParamIcon')}>
              <Icon name={content.highlightedParameter.icon} />
            </div>
          )}
          <div className={cx('highlightedParametersText')}>
            <div
              dangerouslySetInnerHTML={{
                __html: content.highlightedParameter.text,
              }}
            />
          </div>
        </div>
      )}

      {!itemsPending.packages && (
        <div className={cx('addMoreBtnWrap')}>
          <Button
            className={cx('addMoreBtn')}
            wide
            onClick={() => setIsBuyPackagesPopupOpen(true)}
          >
            {content.addMoreBtn}
          </Button>
        </div>
      )}
      <div className={cx('additionalOptions')}>
        <CollapsedContent
          titleClass={cx('optionsTitle')}
          title={content.optionsTitle}
          onOpen={setOptionsBlockOpened}
        >
          <div className={cx('optionsWrap')}>
            {servicesLocal.map((service, index) => (
              <ServiceTile
                key={service.soc}
                suppressedMessage={content.suppressedMessage}
                first={index === 0}
                servicePending={itemsPending.service}
                onChange={onServiceChange}
                service={service}
              />
            ))}
          </div>
        </CollapsedContent>
        {!optionsBlockOpened && (
          <>
            {connectedServices.length === 0 && (
              <img
                className={cx('optionsIcons')}
                src={content.optionsIcons}
                alt=""
              />
            )}
            <HTML as="div" className={cx('optionsDescription')}>
              {connectedServices.length > 0
                ? `У вас подключены: ${connectedServices
                  .map(({ name }) => name)
                  .join(', ')}.`
                : content.optionsDescription}
            </HTML>
          </>
        )}
      </div>
      {isBuyPackagesPopupOpen && (
        <BuyPackagesPopup
          onSetPending={(pending = true) => {
            setItemsPending({
              ...itemsPending,
              packages: pending,
            });
          }}
          onClose={() => setIsBuyPackagesPopupOpen(false)}
          content={content.buyPackagesPopup}
          packages={packages}
        />
      )}
      {activeAccumulator && packagesForActiveAcc && (
        <AccumulatorPopup
          onSetPending={(pending = true) => {
            setItemsPending({
              ...itemsPending,
              packages: pending,
            });
          }}
          onClose={onAccumulatorPopupClose}
          acc={activeAccumulator}
          content={content.accumulatorPopup}
          packages={packagesForActiveAcc}
        />
      )}
      {activeService !== null && (
        <ServicePopup
          onClose={onServicePopupClose}
          onSetPending={({ soc = null, connected }) => {
            setItemsPending({
              ...itemsPending,
              service: soc,
            });

            if (soc) {
              setServicesLocal(
                servicesLocal.map((service) => {
                  if (service.soc === soc) {
                    return {
                      ...service,
                      connected,
                    };
                  }
                  return service;
                }),
              );
            }
          }}
          content={content.servicePopup}
          connectionContent={content.connectionService}
          service={servicesLocal.find(({ soc }) => soc === activeService)}
        />
      )}
    </DataBlocks.Block>
  );
};

YouthTariff.propTypes = {
  data: PropTypes.shape({
    listForYoung: PropTypes.arrayOf(
      PropTypes.shape({
        unit: PropTypes.string,
      }),
    ),
  }),
  content: PropTypes.shape({
    titleLabel: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    addMoreBtn: PropTypes.string,
    suppressedMessage: PropTypes.string,
    optionsTitle: PropTypes.string,
    optionsIcons: PropTypes.string,
    optionsDescription: PropTypes.string,
    accumulatorBlock: PropTypes.object,
    buyPackagesPopup: PropTypes.object,
    accumulatorPopup: PropTypes.object,
    servicePopup: PropTypes.object,
    connectionService: PropTypes.object,
    highlightedParameter: PropTypes.object,
  }),
  packages: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
    }),
  ),
  services: PropTypes.arrayOf(
    PropTypes.shape({
      soc: PropTypes.string,
    }),
  ),
  tariffName: PropTypes.string,
};

const mapStateToProps = (state) => {
  const tariffServicesData = state?.home?.tariffServices?.data;
  return {
    content: tariffServicesData?.content.yonth,
    packages: tariffServicesData?.packages,
    services: tariffServicesData?.services,
    data: state.home.accumulators.data,
    error: state.home.accumulators.error,
    tariffName: state?.home?.profileSummary?.data?.tariffName,
  };
};

export default connect(mapStateToProps)(YouthTariff);
